import { ErfassungListItem, ErfassungListItemStatus, ErfassungListItemTyp, Freigaben, ObjektDatenKey, ObjektTyp, UebernehmenResult, UebernehmenResultErfassungStatus, UebernehmenResultErfassungType } from 'app/core/services/api-client';
import { PermissionHelper } from 'app/core/utils/permission.helper';

export class ErfassungHelper {
  /**
   * Get CSS class for erfassung status badge
   */
  static getStatusClass(status: ErfassungListItemStatus): string | null {
    switch (status) {
      case ErfassungListItemStatus.Neu: 
        return 'badge';
      case ErfassungListItemStatus.InFreigabe: 
        return 'badge bg-blue';
      case ErfassungListItemStatus.Freigegeben: 
        return 'badge bg-green';
      case ErfassungListItemStatus.Zurueckgewiesen: 
        return 'badge bg-orange';
      case ErfassungListItemStatus.Abgeschlossen: 
        return 'badge bg-green';
      case ErfassungListItemStatus.Archiviert: 
        return 'badge bg-gray';
      default:
        return null;
    }
  }

  /**
   * Get navigation URL for an object based on its type
   */
  static objektUrl(objektId: ObjektDatenKey): any[] {
    switch (objektId.typ) {
      case ObjektTyp.Zitat:
        return ['/dossier/citation/', objektId.id];
      case ObjektTyp.Stoff:
        return ['/dossier/substance/', objektId.id];
      case ObjektTyp.Lieferant:
        return ['/dossier/supplier/', objektId.id];
      case ObjektTyp.Spezies:
        return ['/dossier/species/', objektId.id];
      default:
        return ['/home'];
    }
  }

  /**
   * Get navigation URL for freigabe based on type
   */
  static freigabeUrl(erfassung: ErfassungListItem, freigabe: Freigaben): any[] {
    switch (freigabe) {
      case Freigaben.Administrator: 
        return ['/dossier/substance/erfassung', erfassung.id, { roleContext: 'admin' }];
      case Freigaben.Fachlektor:
        return ['/dossier/substance/erfassung', erfassung.id, { roleContext: 'lecturer' }];
      case Freigaben.Zusammenfuehrung:
        return ['/dossier/substance/erfassung', erfassung.id, { roleContext: 'merge' }];
      case Freigaben.Import:
        return ['/import', erfassung.id];
      default:
        return ['/home'];
    }
  }

  /**
   * Check if user can open the erfassung
   */
  static canOpen(erfassung: ErfassungListItem, userData: any): boolean {
    if (!userData) {
      return false;
    }
    
    if (erfassung.typ === ErfassungListItemTyp.Import) {
      return true;
    }
    
    if (erfassung.nutzerUid?.toLowerCase() === userData.sub?.toLowerCase() &&
      (erfassung.status === ErfassungListItemStatus.Neu || 
        erfassung.status === ErfassungListItemStatus.Zurueckgewiesen || 
        erfassung.status === ErfassungListItemStatus.InFreigabe)) {
      // Benutzer ist Owner
      return true;
    }
    
    if (PermissionHelper.hasRight(userData, 130) &&
      (erfassung.status === ErfassungListItemStatus.Neu || 
        erfassung.status === ErfassungListItemStatus.Zurueckgewiesen)) {
      // Benutzer hat Recht "ErfassungUebernehmen"
      return true;
    }
    
    return false;
  }

  /**
   * Get navigation URL to open the erfassung
   */
  static openUrl(erfassung: ErfassungListItem, userData: any): any[] | null {
    if (!userData) {
      return null;
    }
    
    if (!this.canOpen(erfassung, userData)) {
      return null;
    }
    
    if (erfassung.typ === ErfassungListItemTyp.Import) {
      return ['/import', erfassung.id];
    }

    if (erfassung.nutzerUid?.toLowerCase() === userData.sub?.toLowerCase() &&
      erfassung.status === ErfassungListItemStatus.Neu) {
      if (erfassung.typ === ErfassungListItemTyp.Create) {
        return ['/dossier/substance', `N${erfassung.id}`];
      } else if (erfassung.objekt) {
        return ['/dossier/substance', erfassung.objekt.id];
      }
    }
    
    return ['/dossier/substance/erfassung', erfassung.id];
  }

  /**
   * Get navigation URL to open the erfassung
   */
  static openUrlByUebernehmenResult(result: UebernehmenResult): any[] | null {
    if (result.erfassungType === UebernehmenResultErfassungType.Import) {
      return ['/import', result.erfassungId];
    }

    if (result.erfassungStatus === UebernehmenResultErfassungStatus.Neu) {
      if (result.erfassungType === UebernehmenResultErfassungType.Create) {
        return ['/dossier/substance', `N${result.erfassungId}`];
      } else if (result.objekt) {
        return ['/dossier/substance', result.objekt.id];
      }
    }
    return ['/dossier/substance/erfassung', result.erfassungId];
  }

  /**
   * Get title/aria-label for open button
   */
  static getOpenTitle(erfassung: ErfassungListItem, userData: any): string {
    if (this.canOpen(erfassung, userData)) {
      return 'erfassung.actions.open';
    }
    return 'erfassung.actions.cannotOpen';
  }
}
